import { useContext, useEffect, useState } from 'react'
import { Button } from '@/UIComponents2/Button'
import { env } from '@/services/EnvironmentVariables'
import * as Constants from '@/services/Constants'
import { useMutate } from 'restful-react'
import { UserContext, UserContextType } from '@/UserContext'
import { unstable_batchedUpdates } from 'react-dom'
import { exceptionTest } from '@/services/exceptionTestHelper'
import { useTranslation } from 'react-i18next'
import { getAudioEngineInstance } from '@/core/AudioEngine'
import { wsConnection } from '@/services/WSConnectionInstance'
import { useHistory } from 'react-router-dom'
import { Alert } from '@/UIComponents2/Alert'
import { TextField } from '@/UIComponents2/TextField'

export function BugReportField() {
    /**
     * Example error:
     * {
     *     "data": {
     *         "message": "...",
     *         "error": "Not Found",
     *         "statusCode": 404
     *     },
     *     "message": "Failed to fetch: 404",
     *     "status": 404
     * }
     */
    const {
        mutate: sendBugReport,
        loading: isBugReportSending,
        error: sendBugReportError,
    } = useMutate({ verb: 'POST', path: `/v1/contactus-messages` })

    const [bugReportText, set_bugReportText] = useState('')

    const { t } = useTranslation()
    const history = useHistory()

    const { state } = useContext(UserContext) as UserContextType

    const [showThankyouMessage, set_showThankyouMessage] = useState<boolean>(false)
    const [showSubmitButton, set_showSubmitButton] = useState<boolean>(true)
    const [helperText, set_helperText] = useState<string | null>(null)
    const [errorMessage, set_errorMessage] = useState<string | null>(null)

    useEffect(() => {
        if (sendBugReportError) {
            const { data, message, status } = sendBugReportError
            set_errorMessage(`Failed to send bug report: "${message}"`)
        }
    }, [sendBugReportError])

    const onSubmit = () => {
        if (!bugReportText || bugReportText.trim().length === 0) {
            set_helperText(t('Please fill out the form.'))
        } else if (bugReportText.trim().split(' ').length < 5) {
            set_helperText(t('The bug report must have at least 5 words.'))
        } else {
            sendBugReport({
                name: state.profile?.username,
                email: state.profile?.email,
                topic: 'bug-report',
                message: bugReportText,

                os: env.OS,
                osRelease: env.OS_RELEASE,
                processArch: env.PROCESSOR_ARCHITECTURE,
                appVersion: env.APP_VERSION,
                appKind: env.IS_ELECTRON ? Constants.app_kind_desktop : Constants.app_kind_web,
            }).then((result) => {
                const { status } = result || {}
                unstable_batchedUpdates(() => {
                    set_showThankyouMessage(true)
                    set_helperText(null)
                    set_showSubmitButton(false)
                })
            })
        }
    }

    const onTextFieldChange = (value: string) => {
        unstable_batchedUpdates(() => {
            set_bugReportText(value)
            set_helperText(null)
        })

        if (value === 'TEST_MAIN_EXCEPTION') {
            exceptionTest('TEST_MAIN_EXCEPTION')
        } else if (value === 'TEST_MAIN_UNHANDLED_REJECTION') {
            exceptionTest('TEST_MAIN_UNHANDLED_REJECTION')
        } else if (value === 'TEST_CRASH') {
            exceptionTest('TEST_CRASH')
        } else if (value === 'TEST_EXCEPTION') {
            exceptionTest('causeException')
        } else if (value === 'TEST_UNHANDLED_REJECTION') {
            exceptionTest('causeUnhandledRejection')
        } else if (value === 'TEST_AUDIOENGINE_CRASH') {
            getAudioEngineInstance().testCrash()
        } else if (value === 'TEST_UNRESPONSIVE_SIGNALING_SERVER') {
            wsConnection.setTestUnresponsiveSocket()
        } else if (value === 'TEST_CLOSE_SIGNALING_SERVER') {
            wsConnection.testClose(3000)
        } else if (value === 'TEST_KICK_OUT_TO_LOGIN_PAGE') {
            history.push('/login')
        } else if (value === 'TEST_KICK_OUT_TO_MAIN_PAGE') {
            history.push('/')
        }
    }

    return (
        <>
            <div className="mb-2">{t('Please be as detailed as possible')}</div>

            <div className="flex flex-col gap-2 mx-2">
                {showThankyouMessage && (
                    <Alert variant="solid" level="success">
                        {t('Thanks for your feedback!')}
                    </Alert>
                )}
                {errorMessage && (
                    <Alert variant="solid" level="danger">
                        {errorMessage}
                    </Alert>
                )}
            </div>

            <TextField
                value={bugReportText}
                helperText={helperText || undefined}
                onChange={onTextFieldChange}
                rows={6}
                className="w-full"
            />

            {showSubmitButton && (
                <div className="flex justify-end">
                    <Button variant="primary" onClick={onSubmit} disabled={isBugReportSending} className="px-12">
                        {isBugReportSending ? t('Sending...') : t('Send')}
                    </Button>
                </div>
            )}
        </>
    )
}
